import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { RotateCw, XCircle } from "lucide-react";
import {
  api,
  type Alert,
  type Dashboard,
  type Job,
  type JobRun,
  type Page,
} from "@/lib/api";
import { dateTime, relative } from "@/lib/format";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Empty,
  ErrorNote,
  Notice,
  PageHeader,
  Select,
  SortableTh,
  Spinner,
  Stat,
  Table,
  TableFooter,
  Td,
  Th,
  type Tone,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { useClientTable, useServerTable } from "@/lib/table";
import { useMayUse, useScope } from "@/app/auth";

// --- dashboard -----------------------------------------------------------

/**
 * The front page answers one question: does anything need a person right now.
 *
 * The legacy start page was empty, and the answer lived in nine log files and
 * a handful of counters nobody read.
 */
export function DashboardPage() {
  // The overview is the whole system's: it does not follow the company
  // switcher, which stays hidden on it.
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api<Dashboard>("/api/admin/dashboard"),
    refetchInterval: 30_000,
  });

  // Zwei Stücke der Übersicht gehören dem Betreiber: die Meldungen und der
  // Stand der Abrechnung. Wer die Bereiche nicht betreten darf, bekommt sie
  // hier auch nicht abgekürzt zu sehen — und der Server fragt gar nicht erst.
  const mayAlerts = useMayUse("alerts");
  const mayBilling = useMayUse("billing");

  const alerts = useQuery({
    queryKey: ["alerts"],
    queryFn: () => api<Alert[]>("/api/admin/alerts?limit=5"),
    enabled: mayAlerts,
  });

  // Both dashboard cards are short, complete lists, so they order here. The
  // hooks sit above the early returns, where React needs them.
  const billingTable = useClientTable(data?.billing, {
    initial: { key: "process", dir: "asc" },
    pageSize: 25,
  });
  const recentTable = useClientTable(data?.recent, {
    initial: { key: "at", dir: "desc" },
    pageSize: 10,
    accessors: { kind: (r) => eventLabel(r.kind) },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;
  if (!data) return null;

  const needsAttention =
    data.announcementsFailed + data.jobsDead + data.outboxFailed + data.alertsOpen;

  return (
    <>
      <PageHeader
        title="Übersicht"
        description={
          mayBilling
            ? "Der Zustand der Ansagen, der Hintergrundarbeit und der Abrechnung."
            : "Der Zustand der Ansagen und der Hintergrundarbeit."
        }
      />

      {needsAttention === 0 ? (
        <div className="mb-5">
          <Notice tone="success">Nichts braucht gerade einen Menschen.</Notice>
        </div>
      ) : (
        <div className="mb-5">
          <Notice tone="warning">
            {needsAttention} Dinge brauchen Aufmerksamkeit.{" "}
            {mayAlerts && (
              <Link className="underline" to="/alerts">
                Meldungen ansehen
              </Link>
            )}
          </Notice>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Aktive Ansagen" value={data.announcementsActive} tone="success" />
        <Stat label="Geplant" value={data.announcementsPlanned} />
        <Stat
          label="Fehlgeschlagen"
          value={data.announcementsFailed}
          tone={data.announcementsFailed > 0 ? "danger" : "neutral"}
        />
        {mayAlerts && (
          <Stat
            label="Offene Meldungen"
            value={data.alertsOpen}
            tone={data.alertsOpen > 0 ? "warning" : "success"}
          />
        )}

        <Stat label="Jobs wartend" value={data.jobsPending} />
        <Stat label="Jobs laufend" value={data.jobsRunning} />
        <Stat
          label="Jobs endgültig fehlerhaft"
          value={data.jobsDead}
          tone={data.jobsDead > 0 ? "danger" : "neutral"}
        />
        <Stat
          label="Sync-Rückstau"
          value={data.outboxPending}
          tone={data.outboxFailed > 0 ? "warning" : "neutral"}
          hint={data.outboxFailed > 0 ? `${data.outboxFailed} steckengeblieben` : undefined}
        />
      </div>

      <div className={cn("mt-6 grid gap-3", mayBilling && "lg:grid-cols-2")}>
        {mayBilling && (
          <Card className="p-4">
            <h2 className="mb-1 font-medium">Abrechnung {data.billingMonth}</h2>
            <p className="mb-3 text-sm muted">
              Der Monat, der abgerechnet wird, ist immer der vergangene.
            </p>
            {data.billing.length === 0 ? (
              <Empty title="Für diesen Monat ist noch nichts gelaufen" />
            ) : (
              <>
              <Table>
                <thead>
                  <tr>
                    <SortableTh table={billingTable} sortKey="process">
                      Art
                    </SortableTh>
                    <SortableTh table={billingTable} sortKey="state">
                      Zustand
                    </SortableTh>
                    <SortableTh table={billingTable} sortKey="step">
                      Schritt
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {billingTable.rows.map((b) => (
                    <tr key={b.process}>
                      <Td>{b.process}</Td>
                      <Td>
                        <Badge
                          tone={
                            b.state === "success"
                              ? "success"
                              : b.state === "escalated"
                                ? "danger"
                                : "info"
                          }
                        >
                          {b.state}
                        </Badge>
                      </Td>
                      <Td className="muted">{b.step || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TableFooter table={billingTable} noun="Läufe" />
              </>
            )}
          </Card>
        )}

        <Card className="p-4">
          <h2 className="mb-3 font-medium">Zuletzt eingespielt</h2>
          {data.recent.length === 0 ? (
            <Empty title="Noch nichts eingespielt" />
          ) : (
            <>
            <Table>
              <thead>
                <tr>
                  <SortableTh table={recentTable} sortKey="at" first="desc">
                    Zeitpunkt
                  </SortableTh>
                  <SortableTh table={recentTable} sortKey="name">
                    Person
                  </SortableTh>
                  <SortableTh table={recentTable} sortKey="kind">
                    Ereignis
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {recentTable.rows.map((r, i) => (
                  <tr key={i}>
                    <Td className="whitespace-nowrap muted">{relative(r.at)}</Td>
                    <Td>
                      {r.userId ? (
                        <Link className="hover:underline" to={`/users/${r.userId}`}>
                          {r.name}
                        </Link>
                      ) : (
                        r.name
                      )}
                    </Td>
                    <Td className="muted">{eventLabel(r.kind)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <TableFooter table={recentTable} noun="Einspielungen" />
            </>
          )}
        </Card>
      </div>

      {mayAlerts && (alerts.data ?? []).length > 0 && (
        <Card className="mt-6 p-4">
          <h2 className="mb-3 font-medium">Offene Meldungen</h2>
          <ul className="flex flex-col gap-2 text-sm">
            {(alerts.data ?? []).map((a) => (
              <li key={a.id} className="flex items-center gap-2">
                <Badge tone={a.level === "critical" ? "danger" : "warning"}>
                  {a.source}
                </Badge>
                <span>{a.subject}</span>
                <span className="text-xs muted">{relative(a.lastSeen)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function eventLabel(kind: string): string {
  switch (kind) {
    case "vacation_greeting_applied":
      return "Abwesenheitsansage eingespielt";
    case "default_greeting_restored":
      return "Standardansage wiederhergestellt";
    case "default_greeting_applied":
      return "Standardansage aufgenommen";
    default:
      return kind;
  }
}

// --- background work -----------------------------------------------------

const jobTone: Record<string, Tone> = {
  pending: "info",
  running: "warning",
  succeeded: "success",
  failed: "warning",
  dead: "danger",
  cancelled: "neutral",
};

export function JobsPage() {
  const scope = useScope();
  const qc = useQueryClient();
  const [state, setState] = useState("");
  const [kind, setKind] = useState("");
  const [runsFor, setRunsFor] = useState<Job | null>(null);

  // The queue keeps every job it has ever run, so the server pages it.
  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "createdAt", dir: "desc" },
    resetOn: [scope, state, kind],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["jobs", scope, state, kind, table.query],
    queryFn: () => {
      const p = new URLSearchParams(table.query);
      if (state) p.set("state", state);
      if (kind) p.set("kind", kind);
      return api<Page<Job>>(`/api/admin/jobs?${p}`);
    },
    // Work in progress changes on its own, so the view keeps up with it.
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (data) setTotal(data.total);
  }, [data]);

  const act = useMutation({
    mutationFn: ({ id, what }: { id: number; what: "retry" | "cancel" }) =>
      api<void>(`/api/admin/jobs/${id}/${what}`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["jobs"] }),
  });

  return (
    <>
      <PageHeader
        title="Hintergrundarbeit"
        description="Einspielen, Beenden, Abrechnung und Synchronisation. Ein endgültig fehlgeschlagener Job lässt sich hier erneut starten."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Select className="w-48" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">alle Zustände</option>
          <option value="pending">wartend</option>
          <option value="running">läuft</option>
          <option value="succeeded">erledigt</option>
          <option value="failed">Fehler, wird wiederholt</option>
          <option value="dead">endgültig fehlerhaft</option>
          <option value="cancelled">abgebrochen</option>
        </Select>
        <Select className="w-64" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">alle Arten</option>
          <option value="announcement.activate">Ansage einspielen</option>
          <option value="announcement.deactivate">Ansage beenden</option>
          <option value="greeting.default">Standardansage</option>
          <option value="billing.run">Abrechnungslauf</option>
          <option value="outbox.relay">Synchronisation</option>
          <option value="robot.call">Roboter-Anruf</option>
        </Select>
      </div>

      <Card>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4">
            <ErrorNote>{String(error)}</ErrorNote>
          </div>
        ) : !data?.items.length ? (
          <Empty title="Keine Jobs" hint="Es gibt gerade nichts zu tun." />
        ) : (
          <Table>
            <thead>
              <tr>
                <SortableTh table={table} sortKey="kind">
                  Art
                </SortableTh>
                <SortableTh table={table} sortKey="userName">
                  Benutzer
                </SortableTh>
                <SortableTh table={table} sortKey="queue">
                  Queue
                </SortableTh>
                <SortableTh table={table} sortKey="state">
                  Zustand
                </SortableTh>
                <SortableTh table={table} sortKey="attempt" first="desc">
                  Versuche
                </SortableTh>
                <SortableTh table={table} sortKey="runAt" first="desc">
                  Fällig
                </SortableTh>
                <SortableTh table={table} sortKey="lastError">
                  Fehler
                </SortableTh>
                <Th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((j) => (
                <tr key={j.id}>
                  <Td className="font-medium">{j.kind}</Td>
                  <Td>
                    {j.userId ? (
                      <Link className="hover:underline" to={`/users/${j.userId}`}>
                        {j.userName || `#${j.userId}`}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td className="muted">{j.queue}</Td>
                  <Td>
                    <Badge tone={jobTone[j.state] ?? "neutral"}>{j.state}</Badge>
                  </Td>
                  <Td className="tabular-nums">
                    {j.attempt}/{j.maxAttempts}
                  </Td>
                  <Td className="muted">{relative(j.runAt)}</Td>
                  <Td className="max-w-xs truncate muted" title={j.lastError}>
                    {j.lastError || "—"}
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Verlauf"
                        onClick={() => setRunsFor(j)}
                      >
                        Verlauf
                      </Button>
                      {(j.state === "dead" || j.state === "cancelled" ||
                        j.state === "failed") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="erneut versuchen"
                          disabled={act.isPending}
                          onClick={() => act.mutate({ id: j.id, what: "retry" })}
                        >
                          <RotateCw className="size-4" aria-hidden />
                        </Button>
                      )}
                      {(j.state === "pending" || j.state === "running") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="abbrechen"
                          disabled={act.isPending}
                          onClick={() => act.mutate({ id: j.id, what: "cancel" })}
                        >
                          <XCircle className="size-4" aria-hidden />
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <TableFooter table={table} noun="Jobs" />
      </Card>

      {runsFor && <RunsDialog job={runsFor} onClose={() => setRunsFor(null)} />}
    </>
  );
}

/**
 * The attempts of one job, with the log each produced.
 *
 * This is what answers "why did this announcement not go live": which
 * recording was chosen for each block, what the provider said, and when.
 */
function RunsDialog({ job, onClose }: { job: Job; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["jobs", job.id, "runs"],
    queryFn: () => api<JobRun[]>(`/api/admin/jobs/${job.id}/runs`),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${job.kind}${job.userName ? ` · ${job.userName}` : ""} · Verlauf`}
    >
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
        {isLoading && <Spinner />}
        {(data ?? []).length === 0 && !isLoading && (
          <Empty title="Noch kein Versuch protokolliert" />
        )}
        {(data ?? []).map((r) => (
          <Card key={r.id} className="p-3">
            <div className="flex items-center gap-2 text-sm">
              <Badge tone={r.outcome === "ok" ? "success" : "danger"}>
                Versuch {r.attempt}
              </Badge>
              <span className="muted">{dateTime(r.startedAt)}</span>
              <span className="muted">{r.workerId}</span>
              {r.outcome && <span className="muted">{r.outcome}</span>}
            </div>
            {r.error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{r.error}</p>
            )}
            {r.log && (
              <pre className="mt-2 max-h-64 overflow-auto rounded bg-[var(--surface-sunken)] p-2 text-xs">
                {r.log}
              </pre>
            )}
          </Card>
        ))}
        <div className="flex justify-end">
          <Button onClick={onClose}>Schließen</Button>
        </div>
      </div>
    </Dialog>
  );
}
